/**
 * Camera, controls, lights, ground, and the spherical cow.
 *
 * Scene units are kilometres (RENDER_UNITS_PER_M). Axes match physics:
 * x east, y up, z south.
 */
import {
  CanvasTexture,
  CircleGeometry,
  Color,
  DirectionalLight,
  HemisphereLight,
  Mesh,
  MeshStandardNodeMaterial,
  PerspectiveCamera,
  Quaternion,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  Vector3,
} from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DOMAIN_RADIUS_IN_RMAX, RENDER_UNITS_PER_M } from '../config/constants';

export interface SceneHandle {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly controls: OrbitControls;
  readonly cow: Mesh;
  /**
   * Move the cow to a physics position (km). When `rolling`, the mesh turns
   * about the horizontal axis perpendicular to its motion, by distance / visual
   * radius, so the texture rolls without slipping on screen. Allocation-free.
   */
  placeCow(xKm: number, yKm: number, zKm: number, rolling: boolean): void;
  /** Teleport the cow (respawn) without rolling it. */
  snapCow(xKm: number, yKm: number, zKm: number): void;
  /**
   * Keep the view on a moving storm: shifts the ground, the orbit target,
   * and the camera by the change in storm center since the last call.
   */
  followStorm(xKm: number, zKm: number): void;
  setAspect(aspect: number): void;
  dispose(): void;
}

/**
 * Visual radius of the cow, km. A real cow would be invisible at storm
 * scale, so this one is roughly 3 km across. It is a spherical cow; scale
 * is already the least of its approximations.
 */
export const COW_VISUAL_RADIUS_KM = 1.5;

/** Largest Rmax the sliders allow, m. Sizes the ground so it never needs rebuilding. */
const GROUND_RMAX_CEILING_M = 80_000;

export function makeCowTexture(): CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size / 2;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#f4f1ea';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#1c1c1c';
    // Fixed blobs so the texture is deterministic.
    const spots: ReadonlyArray<readonly [number, number, number, number]> = [
      [30, 30, 22, 14],
      [95, 70, 30, 18],
      [160, 25, 18, 12],
      [205, 85, 26, 20],
      [60, 100, 14, 10],
      [130, 110, 20, 9],
      [240, 40, 12, 16],
    ];
    for (const [x, y, rx, ry] of spots) {
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

export function createScene(canvas: HTMLCanvasElement, rMaxM: number): SceneHandle {
  const scene = new Scene();
  scene.background = new Color(0x0d1117);

  const domainKm = DOMAIN_RADIUS_IN_RMAX * rMaxM * RENDER_UNITS_PER_M;
  const camera = new PerspectiveCamera(50, 1, 0.1, domainKm * 20);
  camera.position.set(0, domainKm * 0.9, domainKm * 1.4);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.target.set(0, 0, 0);
  controls.update();

  scene.add(new HemisphereLight(0xbfd4ff, 0x20262e, 1.2));
  const sun = new DirectionalLight(0xffffff, 1.6);
  sun.position.set(-1, 2, 1);
  scene.add(sun);

  const groundRadiusKm = DOMAIN_RADIUS_IN_RMAX * GROUND_RMAX_CEILING_M * RENDER_UNITS_PER_M;
  const groundGeo = new CircleGeometry(groundRadiusKm, 96);
  groundGeo.rotateX(-Math.PI / 2);
  const groundMat = new MeshStandardNodeMaterial({ color: 0x143045, roughness: 0.9 });
  const ground = new Mesh(groundGeo, groundMat);
  scene.add(ground);

  const cowTex = makeCowTexture();
  const cowGeo = new SphereGeometry(COW_VISUAL_RADIUS_KM, 24, 16);
  const cowMat = new MeshStandardNodeMaterial({ map: cowTex, roughness: 0.7 });
  const cow = new Mesh(cowGeo, cowMat);
  scene.add(cow);

  // Scratch for placeCow. The physical cow is ~0.55 m in radius; the visual
  // one is 1.5 km, so its center is lifted to sit on the ground.
  const rollAxis = new Vector3();
  const rollQ = new Quaternion();
  let lastX = 0;
  let lastZ = 0;
  let stormX = 0;
  let stormZ = 0;

  function setCowPosition(xKm: number, yKm: number, zKm: number): void {
    cow.position.set(xKm, yKm > COW_VISUAL_RADIUS_KM ? yKm : COW_VISUAL_RADIUS_KM, zKm);
    lastX = xKm;
    lastZ = zKm;
  }

  return {
    scene,
    camera,
    controls,
    cow,
    placeCow(xKm: number, yKm: number, zKm: number, rolling: boolean): void {
      const dx = xKm - lastX;
      const dz = zKm - lastZ;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (rolling && dist > 0) {
        // up x d = (dz, 0, -dx): the axis a ball rolls about when moving along d.
        rollAxis.set(dz / dist, 0, -dx / dist);
        rollQ.setFromAxisAngle(rollAxis, dist / COW_VISUAL_RADIUS_KM);
        cow.quaternion.premultiply(rollQ);
      }
      setCowPosition(xKm, yKm, zKm);
    },
    snapCow: setCowPosition,
    followStorm(xKm: number, zKm: number): void {
      const dx = xKm - stormX;
      const dz = zKm - stormZ;
      if (dx === 0 && dz === 0) return;
      stormX = xKm;
      stormZ = zKm;
      ground.position.x += dx;
      ground.position.z += dz;
      controls.target.x += dx;
      controls.target.z += dz;
      camera.position.x += dx;
      camera.position.z += dz;
    },
    setAspect(aspect: number): void {
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    },
    dispose(): void {
      controls.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      cowGeo.dispose();
      cowMat.dispose();
      cowTex.dispose();
    },
  };
}
